import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";
import { profileData } from "./data";

/* ── Triangle Cursor Trail (Easter Egg #1) ─────────────── */

function useTriangleTrail(canvasRef, active) {
  const particles = useRef([]);
  const mouse = useRef({ x: 0, y: 0 });
  const animId = useRef(null);

  const PI2 = 2 * Math.PI;

  const drawTriangle = useCallback((ctx, x, y, size, rotation, opacity, color) => {
    const half = size / 2;
    const h = Math.sqrt(size * size - half * half);
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);
    ctx.globalAlpha = opacity;
    ctx.beginPath();
    ctx.moveTo(-half, h / 2);
    ctx.lineTo(half, h / 2);
    ctx.lineTo(0, -h / 2);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
    ctx.restore();
  }, []);

  useEffect(() => {
    if (!active) {
      if (animId.current) cancelAnimationFrame(animId.current);
      particles.current = [];
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext("2d");
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const colors = ["#c8a55a", "#e8e4de", "#7a756e", "#ddb96e", "#a08040"];

    const handleMove = (e) => {
      const currentX = e.clientX;
      const currentY = e.clientY;

      if (mouse.current.x === 0 && mouse.current.y === 0) {
        mouse.current.x = currentX;
        mouse.current.y = currentY;
      }

      const dx = currentX - mouse.current.x;
      const dy = currentY - mouse.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      const steps = Math.max(1, Math.floor(distance / 5));

      for (let i = 0; i < steps; i++) {
        const t = steps === 1 ? 1 : i / steps;
        const interpX = mouse.current.x + dx * t;
        const interpY = mouse.current.y + dy * t;

        for (let j = 0; j < 2; j++) {
          particles.current.push({
            x: interpX + (Math.random() - 0.5) * 15,
            y: interpY + (Math.random() - 0.5) * 15,
            size: 4 + Math.random() * 8,
            rotation: Math.random() * PI2,
            rotationSpeed: (Math.random() - 0.5) * 0.08,
            opacity: 0.6 + Math.random() * 0.4,
            decay: 0.006 + Math.random() * 0.008,
            vx: (Math.random() - 0.5) * 1.5,
            vy: (Math.random() - 0.5) * 1.5 + 0.5,
            color: colors[Math.floor(Math.random() * colors.length)],
          });
        }
      }

      mouse.current.x = currentX;
      mouse.current.y = currentY;
    };

    document.addEventListener("mousemove", handleMove);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.current = particles.current.filter((p) => p.opacity > 0);

      for (const p of particles.current) {
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;
        p.opacity -= p.decay;
        if (p.opacity > 0) {
          drawTriangle(ctx, p.x, p.y, p.size, p.rotation, p.opacity, p.color);
        }
      }

      animId.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resize);
      document.removeEventListener("mousemove", handleMove);
      if (animId.current) cancelAnimationFrame(animId.current);
    };
  }, [active, canvasRef, drawTriangle, PI2]);
}

/* ── Elastic Divider (Easter Egg #2) ───────────────────── */

let audioCtx = null;
const playString = (frequency, intensity) => {
  if (!audioCtx) {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (AudioContext) audioCtx = new AudioContext();
  }
  if (!audioCtx) return;
  if (audioCtx.state === "suspended") audioCtx.resume();

  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();

  // Triangle wave for string-like sound
  osc.type = "triangle";
  osc.frequency.setValueAtTime(frequency, audioCtx.currentTime);

  // Pluck envelope: cubic so volume stays low until big pulls
  const vol = Math.pow(Math.min(1, intensity), 3) * 0.5;
  gain.gain.setValueAtTime(vol, audioCtx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 1.5);

  osc.connect(gain);
  gain.connect(audioCtx.destination);

  osc.start();
  osc.stop(audioCtx.currentTime + 1.5);
};

function ElasticDivider({ className, frequency, onPluck }) {
  const svgRef = useRef(null);
  const isDragging = useRef(false);
  const deflection = useRef(0);
  const velocity = useRef(0);
  const animId = useRef(null);
  const [, forceRender] = useState(0);

  const WIDTH = 620;
  const HEIGHT = 40;
  const MID_Y = HEIGHT / 2;
  const STIFFNESS = 0.15;
  const DAMPING = 0.82;
  const THRESHOLD = 0.3;

  const getRelativeY = useCallback((e, svg) => {
    const rect = svg.getBoundingClientRect();
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return clientY - rect.top - MID_Y;
  }, [MID_Y]);

  const startPhysics = useCallback(() => {
    if (animId.current) return;

    const step = () => {
      if (isDragging.current) {
        animId.current = requestAnimationFrame(step);
        return;
      }

      const force = -STIFFNESS * deflection.current;
      velocity.current += force;
      velocity.current *= DAMPING;
      deflection.current += velocity.current;

      if (Math.abs(deflection.current) < THRESHOLD && Math.abs(velocity.current) < THRESHOLD) {
        deflection.current = 0;
        velocity.current = 0;
        animId.current = null;
        forceRender((n) => n + 1);
        return;
      }

      forceRender((n) => n + 1);
      animId.current = requestAnimationFrame(step);
    };

    animId.current = requestAnimationFrame(step);
  }, [STIFFNESS, DAMPING, THRESHOLD]);

  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    deflection.current = getRelativeY(e, svgRef.current);
    velocity.current = 0;
    forceRender((n) => n + 1);

    const onMove = (ev) => {
      if (!isDragging.current) return;
      ev.preventDefault();
      deflection.current = getRelativeY(ev, svgRef.current);
      forceRender((n) => n + 1);
    };

    const onUp = () => {
      isDragging.current = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
      
      const deflectionAmt = Math.abs(deflection.current);
      if (deflectionAmt > 12 && frequency) {
        playString(frequency, deflectionAmt / HEIGHT);
        if (onPluck) onPluck(frequency);
      }
      
      startPhysics();
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onUp);
  }, [getRelativeY, startPhysics, frequency, onPluck]);

  useEffect(() => {
    return () => {
      if (animId.current) cancelAnimationFrame(animId.current);
    };
  }, []);

  const cy = MID_Y + deflection.current;
  const d = `M 0 ${MID_Y} Q ${WIDTH / 2} ${cy} ${WIDTH} ${MID_Y}`;

  return (
    <div className={`elastic-divider ${className || ""}`}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        className="elastic-divider__svg"
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
      >
        <path d={d} stroke="#444" strokeWidth="1" fill="none" />
      </svg>
    </div>
  );
}

/* ── Tetris Rain (Easter Egg #3) ───────────────────────── */
/* Type "tetris" anywhere on the page — no cooldown, stacks! */

function useTetrisRain(canvasRef) {
  const pieces = useRef([]);
  const animId = useRef(null);
  const bufferRef = useRef("");

  const SHAPES = useRef([
    { blocks: [[0,0],[0,1],[1,0],[1,1]], color: "#c8a55a" },       // O
    { blocks: [[0,0],[0,1],[0,2],[0,3]], color: "#e8e4de" },       // I
    { blocks: [[0,0],[1,0],[1,1],[1,2]], color: "#ddb96e" },       // J
    { blocks: [[0,2],[1,0],[1,1],[1,2]], color: "#a08040" },       // L
    { blocks: [[0,1],[0,2],[1,0],[1,1]], color: "#7a756e" },       // S
    { blocks: [[0,0],[0,1],[1,1],[1,2]], color: "#c8a55a" },       // Z
    { blocks: [[0,1],[1,0],[1,1],[1,2]], color: "#e8e4de" },       // T
  ]).current;

  const spawnWave = useCallback(() => {
    const w = window.innerWidth;
    const cellSize = 14;

    for (let i = 0; i < 35; i++) {
      const shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
      pieces.current.push({
        x: Math.random() * w,
        y: -Math.random() * window.innerHeight * 0.8 - 40,
        vy: 1.5 + Math.random() * 2.5,
        rotation: Math.floor(Math.random() * 4) * (Math.PI / 2),
        rotationSpeed: (Math.random() - 0.5) * 0.02,
        opacity: 0.3 + Math.random() * 0.4,
        cellSize,
        blocks: shape.blocks,
        color: shape.color,
      });
    }
  }, [SHAPES]);

  // Standalone animation loop — runs whenever there are pieces
  const startLoop = useCallback(() => {
    if (animId.current) return; // Already running

    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const ctx = canvas.getContext("2d");

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      pieces.current = pieces.current.filter(
        (p) => p.y < window.innerHeight + 100
      );

      if (pieces.current.length === 0) {
        animId.current = null;
        return;
      }

      for (const p of pieces.current) {
        p.y += p.vy;
        p.rotation += p.rotationSpeed;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;

        for (const [r, c] of p.blocks) {
          ctx.fillRect(
            c * p.cellSize,
            r * p.cellSize,
            p.cellSize - 1,
            p.cellSize - 1
          );
        }

        ctx.restore();
      }

      animId.current = requestAnimationFrame(animate);
    };

    animId.current = requestAnimationFrame(animate);
  }, [canvasRef]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key.length !== 1) return;

      bufferRef.current += e.key.toLowerCase();
      if (bufferRef.current.length > 6) {
        bufferRef.current = bufferRef.current.slice(-6);
      }

      if (bufferRef.current === "tetris") {
        bufferRef.current = "";
        spawnWave();
        startLoop();
      }
    };

    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      if (animId.current) cancelAnimationFrame(animId.current);
    };
  }, [spawnWave, startLoop]);
}

/* ── DevTools Console Greeting (Easter Egg #4) ─────────── */

function useConsoleGreeting() {
  useEffect(() => {
    const lines = [
      "",
      "%c ╔════════════════════════════════════════╗ ",
      " ║                                        ║ ",
      " ║             kevin huang ▸              ║ ",
      " ║                                        ║ ",
      " ║    curious enough to open devtools?    ║ ",
      " ║    type 'tetris' for a surprise.       ║ ",
      " ║    try entering the Konami Code too.   ║ ",
      " ║                                        ║ ",
      " ╚════════════════════════════════════════╝ ",
      "",
    ];

    console.log(
      lines.join("\n"),
      "color: #c8a55a; font-family: monospace; font-size: 12px; line-height: 1.4;"
    );
  }, []);
}

/* ── Clickable Footer Date Formats (Easter Egg #5) ─────── */

function useFooterDateCycle() {
  const [formatIndex, setFormatIndex] = useState(0);
  const now = new Date();

  const formats = [
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`,
    `${Math.floor(now.getTime() / 1000)}`,
    `${(now.getFullYear() - 2323 + (now.getMonth() / 12)).toFixed(1)}`,
    "just now",
  ];

  const labels = [
    "last compiled ",
    "last compiled ",
    "stardate ",
    "last compiled: ",
  ];

  const cycle = useCallback(() => {
    setFormatIndex((prev) => (prev + 1) % formats.length);
  }, [formats.length]);

  return {
    dateText: formats[formatIndex],
    dateLabel: labels[formatIndex],
    cycleDate: cycle,
  };
}

/* ── Tab-Away Title (Easter Egg #6) ────────────────────── */
/* Switching tabs changes the page title; coming back restores it */

function useTabTitle() {
  useEffect(() => {
    const original = document.title;

    const handleVisibility = () => {
      if (document.hidden) {
        document.title = "come back pls!";
      } else {
        document.title = original;
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      document.title = original;
    };
  }, []);
}

/* ── Konami Code (Easter Egg #7) ───────────────────────── */
/* ↑↑↓↓←→←→BA triggers a brief accent color pulse */

function useKonamiCode() {
  const bufferRef = useRef([]);

  useEffect(() => {
    const SEQUENCE = ["ArrowUp","ArrowUp","ArrowDown","ArrowDown","ArrowLeft","ArrowRight","ArrowLeft","ArrowRight","b","a","Enter"];
    const handleKey = (e) => {
      bufferRef.current.push(e.key);
      if (bufferRef.current.length > SEQUENCE.length) {
        bufferRef.current = bufferRef.current.slice(-SEQUENCE.length);
      }

      if (bufferRef.current.length === SEQUENCE.length &&
          bufferRef.current.every((k, i) => k === SEQUENCE[i])) {
        bufferRef.current = [];
        document.body.classList.add("konami-active");
        setTimeout(() => {
          document.body.classList.remove("konami-active");
        }, 8500);
      }
    };

    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);
}

/* ── App ───────────────────────────────────────────────── */

function App() {
  const [trailActive, setTrailActive] = useState(false);
  const [triangleHovered, setTriangleHovered] = useState(false);
  const [justClicked, setJustClicked] = useState(false);
  const trailCanvasRef = useRef(null);
  const tetrisCanvasRef = useRef(null);

  useTriangleTrail(trailCanvasRef, trailActive);
  useTetrisRain(tetrisCanvasRef);
  useConsoleGreeting();
  useTabTitle();
  useKonamiCode();
  const { dateText, dateLabel, cycleDate } = useFooterDateCycle();

  const toggleTrail = useCallback(() => {
    setTrailActive((prev) => !prev);
    setJustClicked(true);
  }, []);

  const handleTriangleEnter = useCallback(() => setTriangleHovered(true), []);
  const handleTriangleLeave = useCallback(() => {
    setTriangleHovered(false);
    setJustClicked(false);
  }, []);

  const [triangleRotation, setTriangleRotation] = useState(90);
  const rotationRef = useRef(90);

  useEffect(() => {
    const targetIsDown = justClicked 
      ? (trailActive === triangleHovered) 
      : (trailActive !== triangleHovered);

    const currentIsDown = (Math.round((rotationRef.current - 90) / 180) % 2 !== 0);

    if (targetIsDown !== currentIsDown) {
      rotationRef.current += 180;
      setTriangleRotation(rotationRef.current);
    }
  }, [trailActive, triangleHovered, justClicked]);

  const pluckSequence = useRef([]);
  const handlePluck = useCallback((freq) => {
    const chordFrequencies = [261.63, 329.63, 392.00, 523.25];
    pluckSequence.current.push(freq);
    if (pluckSequence.current.length > 4) {
      pluckSequence.current.shift();
    }
    
    const isMatch = pluckSequence.current.length === 4 && 
                    pluckSequence.current.every((f, i) => Math.abs(f - chordFrequencies[i]) < 0.1);
                    
    if (isMatch) {
      pluckSequence.current = [];
      setTimeout(() => {
         chordFrequencies.forEach((f, i) => {
           setTimeout(() => playString(f, 0.82), i * 20);
         });
        //  setTimeout(() => {
        //    chordFrequencies.forEach(f => playString(f, 0.88));
        //  }, chordFrequencies.length * 90 + 100);
      }, 1500);
    }
  }, []);

  return (
    <div className="App">
      <canvas ref={trailCanvasRef} className="cursor-canvas" />
      <canvas ref={tetrisCanvasRef} className="cursor-canvas" />

      {/* Header */}
      <header className="header stagger" id="header">
        <h1 className="header__name">
          {profileData.name}
          <span
            className={`header__triangle${trailActive ? ' header__triangle--active' : ''}`}
            onClick={toggleTrail}
            onMouseEnter={handleTriangleEnter}
            onMouseLeave={handleTriangleLeave}
            title="click me!"
          >
            <svg 
              width="1em" 
              height="1em" 
              viewBox="0 0 24 24" 
              fill="currentColor" 
              style={{ 
                verticalAlign: "-0.22em",
                transform: `rotate(${triangleRotation}deg)`,
                transformOrigin: "50% 50%",
                transition: "transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)"
              }}
            >
              <polygon points="12,2 20.66,17 3.34,17" />
            </svg>
          </span>
        </h1>
        <p className="header__tagline">{profileData.tagline}</p>
      </header>

      <ElasticDivider className="stagger" frequency={261.63} onPluck={handlePluck} /> {/* C4 */}

      {/* About */}
      <section className="section stagger" id="about">
        <h2 className="section__title">about</h2>
        <div className="section__body">
          <p>{profileData.about}</p>
          {profileData.coursework && (
            <p className="coursework-line"><strong>coursework:</strong> {profileData.coursework.join(" · ")}</p>
          )}
        </div>
      </section>

      {/* Now */}
      {profileData.now && (
        <section className="section stagger" id="now">
          <h2 className="section__title">now</h2>
          <div className="section__body">
            <p>{profileData.now}</p>
          </div>
        </section>
      )}

      <ElasticDivider className="stagger" frequency={329.63} onPluck={handlePluck} /> {/* E4 */}

      {/* Experience */}
      {profileData.experience && profileData.experience.length > 0 && (
        <section className="section stagger" id="experience">
          <h2 className="section__title">experience</h2>
          <ul className="experience-list">
            {profileData.experience.map((e, i) => (
              <li key={i} className="experience-item">
                <div className="experience-item__header">
                  <span className="experience-item__role">{e.role}</span>
                  <span className="experience-item__period">{e.period}</span>
                </div>
                <span className="experience-item__company">{e.company}</span>
                <span className="experience-item__desc">{e.desc}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Projects */}
      <section className="section stagger" id="projects">
        <h2 className="section__title">projects</h2>
        <ul className="projects-list">
          {profileData.projects.map((p, i) => (
            <li key={i} className="project">
              <span className="project__title">
                {p.link ? (
                  <a href={p.link} target="_blank" rel="noopener noreferrer">
                    {p.title}
                  </a>
                ) : (
                  p.title
                )}
              </span>
              <span className="project__desc">{p.desc}</span>
              <span className="project__tag">{p.tag}</span>
            </li>
          ))}
        </ul>
      </section>

      <ElasticDivider className="stagger" frequency={392.00} onPluck={handlePluck} /> {/* G4 */}

      {/* Links */}
      <section className="section stagger" id="links">
        <div className="links-row">
          {profileData.links.map((l, i) => (
            <React.Fragment key={i}>
              <a
                href={l.href}
                target={l.href.startsWith("mailto:") ? undefined : "_blank"}
                rel={l.href.startsWith("mailto:") ? undefined : "noopener noreferrer"}
              >
                {l.label}
              </a>
              {i < profileData.links.length - 1 && <span className="links-row__sep">·</span>}
            </React.Fragment>
          ))}
        </div>
      </section>

      <ElasticDivider className="stagger" frequency={523.25} onPluck={handlePluck} /> {/* C5 */}

      {/* Footer */}
      <footer className="footer stagger" id="footer">
        {dateLabel}
        <span className="footer__date" onClick={cycleDate} title="click to cycle formats">
          {dateText}
        </span>
      </footer>
    </div>
  );
}

export default App;
